import axios from 'axios';
import pako from 'pako';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

type HotcoinKlineResponse = {
  code: number;
  msg: string;
  time: number;
  data: KLine[];
};

export class HotcoinStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  wsDepth: ReconnectingWebSocket | undefined;
  wsTicker: ReconnectingWebSocket | undefined;
  wsKline: ReconnectingWebSocket | undefined;
  wsTrade: ReconnectingWebSocket | undefined;

  bids: [string, string][] = [];
  asks: [string, string][] = [];
  pair: string;
  symbolBase: string;
  displayDepth: number;
  fetchDepth = 1000;
  precision: number;
  selectedPrecision: number;

  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toLowerCase()}_${quote.toLowerCase()}`;
    this.symbolBase = `${base.toLowerCase()}`;

    this.displayDepth = orderbookDepth ?? 20;

    this.bids = [];
    this.asks = [];

    this.precision = 1;
    this.selectedPrecision = 1;

    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  private async blobToString(blob: Blob): Promise<string> {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      if (uint8Array[0] === 0x1f && uint8Array[1] === 0x8b) {
        return pako.ungzip(uint8Array, { to: 'string' });
      }

      return new TextDecoder().decode(uint8Array);
    } catch (error) {
      throw new Error(`Failed to process Blob: ${error}`);
    }
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await axios.get<{ data: { pricePrecision: number; baseCurrency: string }[] }>(
        'https://api.hotcoinfin.com/v1/common/symbols',
      );

      const foundSymbol = res.data.data.find(
        symbol => symbol.baseCurrency.toLowerCase() === this.symbolBase.toLowerCase(),
      );

      if (foundSymbol) {
        this.precision = foundSymbol.pricePrecision;
        this.selectedPrecision = foundSymbol.pricePrecision;
        this.precisionUpdate(foundSymbol.pricePrecision);
      }
    } catch (error) {
      console.error('Hotcoin ticker error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const res = await axios.get<{
        code: number;
        msg: string;
        time: number;
        data: {
          depth: { asks: [number, number][]; bids: [number, number][] };
        };
      }>(`https://api.hotcoinfin.com/v1/depth`, {
        params: { symbol: this.pair, step: 60 },
      });

      const { asks, bids } = res.data.data.depth;
      this.asks = asks.map(([p, q]) => [p.toString(), q.toString()]);
      this.bids = bids.map(([p, q]) => [p.toString(), q.toString()]);

      const orderbook = aggregateOrderbook(
        this.asks,
        this.bids,
        this.selectedPrecision,
        this.displayDepth,
      );

      this.onOrderbookUpdate(orderbook);

      return orderbook;
    } catch (error) {
      console.error('Hotcoin orderbook error:', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    interval,
  }: {
    base: string;
    quote: string;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const hotcoinInterval: Partial<Record<IChartTimeframe, number>> = {
      '1m': 60,
      '5m': 300,
      '15m': 900,
      '1h': 3600,
      '4h': 14400,
      '24h': 86400,
      '168h': 604800,
    };

    const intervalValue = hotcoinInterval[interval];

    try {
      const res = await axios.get<HotcoinKlineResponse>(`https://api.hotcoinfin.com/v1/ticker`, {
        params: {
          symbol: `${base.toLowerCase()}_${quote.toLowerCase()}`,
          step: intervalValue,
        },
      });

      return (res.data.data || []).map(el => [el[0], el[1], el[2], el[3], el[4], el[5]] as KLine);
    } catch (error) {
      console.error('Hotcoin klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({
    base,
    quote,
    limit,
  }: {
    base: string;
    quote: string;
    limit: number;
  }): Promise<TradeSocket[]> => {
    try {
      const res = await axios.get<{
        code: number;
        msg: string;
        time: number;
        data: {
          trades: {
            price: number;
            amount: number;
            en_type: 'bid' | 'ask';
            ts: string;
          }[];
        };
      }>(`https://api.hotcoinfin.com/v1/trade`, {
        params: { symbol: `${base.toLowerCase()}_${quote.toLowerCase()}`, count: limit },
      });

      return res.data.data.trades.reverse().map(item => ({
        price: item.price.toString(),
        quantity: item.amount.toString(),
        timestamp: item.ts,
        side: item.en_type === 'ask' ? 'sell' : 'buy',
      }));
    } catch (error) {
      console.error('Hotcoin trades error:', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    const wsUrl = 'wss://wss.hotcoinfin.com/trade/multiple';
    const hotcoinInterval =
      {
        '1m': '1m',
        '5m': '5m',
        '15m': '15m',
        '1h': '1h',
        '4h': '4h',
        '1d': '1d',
        '168h': '1w',
      }[interval] || '1m';

    const ws = new ReconnectingWebSocket(wsUrl, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          action: 'subscribe',
          sub: `market.${this.pair}.kline.${hotcoinInterval}`,
        }),
      );

      ws.send(
        JSON.stringify({
          action: 'subscribe',
          sub: `market.${this.pair}.trade.depth`,
        }),
      );

      ws.send(
        JSON.stringify({
          action: 'subscribe',
          sub: `market.${this.pair}.24h.tickers`,
        }),
      );

      ws.send(
        JSON.stringify({
          action: 'subscribe',
          sub: `market.${this.pair}.trade.detail`,
        }),
      );
    };

    ws.onmessage = async event => {
      let data;

      if (event.data instanceof Blob) {
        const text = await this.blobToString(event.data);
        data = JSON.parse(text);
      } else {
        data = JSON.parse(event.data);
      }

      if (!data.data) return;

      if (data.ch === `market.${this.pair}.kline.${hotcoinInterval}`) {
        const [time, open, high, low, close, volume] = data.data[data.data.length - 1];

        this.klineUpdate([
          Math.floor(Number(time) / 1000),
          normalizeNumber(open),
          normalizeNumber(high),
          normalizeNumber(low),
          normalizeNumber(close),
          normalizeNumber(volume),
        ]);
      }

      if (data.ch === `market.${this.pair}.trade.depth`) {
        const asks = mergeOrderBook(this.asks, data.data.asks).sort(sortOrderbookFunc);
        const bids = mergeOrderBook(this.bids, data.data.bids).sort(sortOrderbookFunc);

        this.asks = asks;
        this.bids = bids;

        this.onOrderbookUpdate(
          aggregateOrderbook(
            asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
            bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
            this.selectedPrecision,
          ),
        );
      }

      if (data.ch === `market.${this.pair}.24h.tickers` && data.data) {
        const { last, high, close, volume, low, amount, change } = data.data;

        this.onTickerUpdate({
          baseVolume: volume,
          change: normalizeNumber(Number(change) * 100),
          close,
          high,
          last: Number(last).toFixed(this.precision),
          low,
          quoteVolume: amount,
        });
      }

      if (data.ch === `market.${this.pair}.trade.detail`) {
        const trade = data.data?.[0];

        if (trade) {
          this.onTradeUpdate({
            price: trade.price,
            quantity: trade.amount,
            timestamp: trade.ts,
            side: trade.direction,
          });
        }
      }
    };
  };

  closeWebSocket() {
    this.wsKline?.close();
    this.wsDepth?.close();
    this.wsTicker?.close();
    this.wsTrade?.close();
  }

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;

    const orderbook = aggregateOrderbook(
      this.asks,
      this.bids,
      this.selectedPrecision,
      this.displayDepth,
    );

    this.onOrderbookUpdate(orderbook);
  };

  setOrderbookDepth(depth: number) {
    this.displayDepth = depth;
    this.fetchOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec }, (_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await this.fetchOrderbook();
  };

  static init = async (args: IInitArgs) => {
    const instance = new HotcoinStrategy(args);
    await bootstrapStrategy(instance);
    return instance;
  };
}
